import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Switch,
  FormControl,
  FormControlLabel,
  FormLabel,
  FormHelperText,
  Typography,
} from "@mui/material";
import { colors } from "../../../../theme";

/**
 * CommonSwitch Component
 * Centralized, reusable Switch toggle component adhering to AutoBET design tokens.
 *
 * Features:
 * - Standalone or with integrated FormLabel (icon, label, required asterisk)
 * - Built-in dynamic status label ("Active" / "Inactive" or custom text)
 * - Flexible placement ('end' | 'start' | 'top' | 'bottom')
 * - Built-in error and helper text integration
 */
const CommonSwitch = ({
  checked = false,
  onChange,
  activeLabel = "Active",
  inactiveLabel = "Inactive",
  switchLabel,
  showStatusLabel = true,
  labelPlacement = "end",
  size = "medium",
  color = "primary",
  disabled = false,
  id,
  name,

  // Standalone Form Header Props
  label,
  icon,
  iconColor,
  iconSize = 16,
  required = false,
  requiredColor,
  labelSx = {},

  // Helper & Error Props
  error = false,
  helperText,
  errorText,
  helperTextSx = {},
  statusLabelSx = {},
  formControlSx = {},

  sx = {},
  switchProps = {},
  ...props
}) => {
  const isError = Boolean(error);
  const resolvedRequiredColor =
    requiredColor || colors.dashboard?.status?.dangerText || colors.error?.main || "#ef4444";
  const resolvedHelperText =
    typeof error === "string" ? error : isError ? errorText || helperText : helperText;

  const currentStatusLabel =
    switchLabel !== undefined
      ? typeof switchLabel === "function"
        ? switchLabel(checked)
        : switchLabel
      : checked
      ? activeLabel
      : inactiveLabel;

  const switchControl = (
    <Switch
      id={id}
      name={name}
      checked={Boolean(checked)}
      onChange={onChange}
      disabled={disabled}
      size={size}
      color={color}
      {...switchProps}
      {...props}
    />
  );

  const controlWithLabel = showStatusLabel ? (
    <FormControlLabel
      control={switchControl}
      labelPlacement={labelPlacement}
      disabled={disabled}
      label={
        currentStatusLabel ? (
          <Typography
            component="span"
            sx={{
              fontSize: size === "small" ? "13px" : "14px",
              fontWeight: 500,
              color: disabled ? colors.text.disabled : colors.text.primary,
              userSelect: "none",
              ...statusLabelSx,
            }}
          >
            {currentStatusLabel}
          </Typography>
        ) : null
      }
      sx={{
        m: 0,
        ...sx,
      }}
    />
  ) : (
    <Box sx={{ display: "inline-flex", alignItems: "center", ...sx }}>
      {switchControl}
    </Box>
  );

  if (label || icon || resolvedHelperText) {
    return (
      <FormControl
        fullWidth
        error={isError}
        disabled={disabled}
        sx={{
          ...formControlSx,
        }}
      >
        {(label || icon) && (
          <FormLabel
            htmlFor={id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
              fontWeight: 600,
              fontSize: "14px",
              color: colors.slate[700],
              "&.Mui-focused": {
                color: colors.slate[700],
              },
              ...labelSx,
            }}
          >
            {icon && (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: iconColor || colors.slate[500],
                  flexShrink: 0,
                  "& svg": {
                    width: iconSize,
                    height: iconSize,
                  },
                }}
              >
                {icon}
              </Box>
            )}

            {label && (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                {label}
                {required && (
                  <Box
                    component="span"
                    sx={{
                      color: resolvedRequiredColor,
                      ml: 0.5,
                    }}
                  >
                    *
                  </Box>
                )}
              </Box>
            )}
          </FormLabel>
        )}

        {controlWithLabel}

        {resolvedHelperText && (
          <FormHelperText
            error={isError}
            sx={{
              fontSize: "12px",
              mt: 0.75,
              mx: 0.5,
              color: isError
                ? resolvedRequiredColor
                : colors.slate[500],
              ...helperTextSx,
            }}
          >
            {resolvedHelperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  return controlWithLabel;
};

CommonSwitch.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  activeLabel: PropTypes.node,
  inactiveLabel: PropTypes.node,
  switchLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  showStatusLabel: PropTypes.bool,
  labelPlacement: PropTypes.oneOf(["end", "start", "top", "bottom"]),
  size: PropTypes.oneOf(["small", "medium"]),
  color: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,

  // Standalone Header
  label: PropTypes.node,
  icon: PropTypes.node,
  iconColor: PropTypes.string,
  iconSize: PropTypes.number,
  required: PropTypes.bool,
  requiredColor: PropTypes.string,
  labelSx: PropTypes.object,

  // Error & Helper
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  helperText: PropTypes.node,
  errorText: PropTypes.node,
  helperTextSx: PropTypes.object,
  statusLabelSx: PropTypes.object,
  formControlSx: PropTypes.object,

  sx: PropTypes.object,
  switchProps: PropTypes.object,
};

export default CommonSwitch;
